import { createAction } from '@reduxjs/toolkit';
import { buildReducer } from '../../utils/redux/getReduxModuleByFeatureName';

// @TIMEPLAN_SLOT_WAGE — CREW-1184.
//
// The wage agreed for ONE timeplan slot. Its own slice rather than a few
// more fields on the timeplans slice, because the board index serialises
// every slot in the planner's date range and this is fetched only while
// one slot's form is open.

// The six lines that come from the owner's wage matrix
// (`/owners/:id/wage`) for this role. Read-only here: the API refuses to
// write them, and they follow the rate card until the slot is approved.
export const MATRIX_FIELDS = [
  'basic',
  'overtime',
  'overtime_rate',
  'leave',
  'fixed_bonus',
  'trade_allow',
];

// The three lines the slot form may write — the only ones the API accepts.
// `owners_bonus` and `seniority_bonus` are in the owner's wage matrix too
// and are seeded from it server-side, but stay editable so a coordinator
// can correct them; `other` is free. Single source of truth for the form,
// the selector and the saga; `wages` / `total` are derived server-side.
export const EDITABLE_FIELDS = [
  'owners_bonus',
  'seniority_bonus',
  'other',
];

// Everything the form needs to hold, matrix lines included, so the
// computed TOTAL can be shown without a second round trip.
//
// `component_lines` (CREW-1101) is in here for exactly that reason and it is
// easy to miss: the selector `pick`s this list, so a line the owner pays that
// has no column of its own would arrive from the API, be dropped here, and the
// form's TOTAL would silently disagree with the server's by the amount of it.
// Read-only like the matrix fields — the write path picks EDITABLE_FIELDS.
export const WAGE_FIELDS = [
  ...MATRIX_FIELDS, ...EDITABLE_FIELDS, 'matrix_frozen_at', 'component_lines',
];

const initialState = {
  data: null,
  // Which slot `data` belongs to. Also the "already fetched" guard — the
  // load runs off two triggers (form mount and the slot list landing) so
  // that a deep link still gets its wage once the slot itself arrives.
  loadedForId: null,
  loading: false,
  saving: false,
  error: null,
};

export const constants = {
  LOAD_SLOT_WAGE_REQUEST: '@TIMEPLAN_SLOT_WAGE/LOAD_REQUEST',
  LOAD_SLOT_WAGE_SUCCESS: '@TIMEPLAN_SLOT_WAGE/LOAD_SUCCESS',
  LOAD_SLOT_WAGE_ERROR: '@TIMEPLAN_SLOT_WAGE/LOAD_ERROR',

  SAVE_SLOT_WAGE_SUCCESS: '@TIMEPLAN_SLOT_WAGE/SAVE_SUCCESS',
  SAVE_SLOT_WAGE_ERROR: '@TIMEPLAN_SLOT_WAGE/SAVE_ERROR',
  SAVE_SLOT_WAGE_STARTED: '@TIMEPLAN_SLOT_WAGE/SAVE_STARTED',

  RESYNC_SLOT_WAGE_REQUEST: '@TIMEPLAN_SLOT_WAGE/RESYNC_REQUEST',

  RESET_SLOT_WAGE: '@TIMEPLAN_SLOT_WAGE/RESET',
};

export const actions = {
  loadSlotWageRequest: createAction(constants.LOAD_SLOT_WAGE_REQUEST),
  loadSlotWageSuccess: createAction(constants.LOAD_SLOT_WAGE_SUCCESS),
  loadSlotWageError: createAction(constants.LOAD_SLOT_WAGE_ERROR),

  saveSlotWageStarted: createAction(constants.SAVE_SLOT_WAGE_STARTED),
  saveSlotWageSuccess: createAction(constants.SAVE_SLOT_WAGE_SUCCESS),
  saveSlotWageError: createAction(constants.SAVE_SLOT_WAGE_ERROR),

  resyncSlotWageRequest: createAction(constants.RESYNC_SLOT_WAGE_REQUEST),

  resetSlotWage: createAction(constants.RESET_SLOT_WAGE),
};

const actionHandlers = {
  [constants.LOAD_SLOT_WAGE_REQUEST]: (state) => ({ ...state, loading: true, error: null }),
  [constants.LOAD_SLOT_WAGE_SUCCESS]: (state, { payload: { timePlanId, slotWage } }) => ({
    ...state,
    data: slotWage,
    loadedForId: timePlanId,
    loading: false,
  }),
  [constants.LOAD_SLOT_WAGE_ERROR]: (state, { payload: error }) => ({
    ...state,
    loading: false,
    error,
  }),

  [constants.SAVE_SLOT_WAGE_STARTED]: (state) => ({ ...state, saving: true, error: null }),
  [constants.SAVE_SLOT_WAGE_SUCCESS]: (state, { payload: slotWage }) => ({
    ...state,
    data: slotWage,
    saving: false,
  }),
  [constants.SAVE_SLOT_WAGE_ERROR]: (state, { payload: error }) => ({
    ...state,
    saving: false,
    error,
  }),

  [constants.RESET_SLOT_WAGE]: () => ({ ...initialState }),
};

export const reducer = buildReducer(initialState, actionHandlers);

export const selector = (state) => state.timeplanSlotWage;
